import { useTheme } from "@emotion/react";
import type { ColorItem, Padding, SizeName } from "@towni/common";
import { colorAsString } from "@towni/common";
import { paddingToCssValue } from "../padding";

type LineStyle = "solid" | "dashed" | "dotted";

type HorizontalProps = { [key in SizeName]?: boolean } & {
    size?: number;
    color?: ColorItem;
    padding?: Padding;
    fillParentWidth?: boolean;
    className?: string;
    lineStyle?: LineStyle;
    dashLength?: number; // Optional dash length for dashed style
    dotsDashGap?: number; // Optional gap between dots or dashes
};

const HorizontalLine = (props: HorizontalProps) => {
    const theme = useTheme();
    const { fillParentWidth, lineStyle = "solid" } = props;
    const size = props.size ?? 1;

    const backgroundColor = props.color || theme.colors.default.border;
    const color =
        (backgroundColor && colorAsString(backgroundColor)) || "unset";

    // Default dash and gap values
    const dashLength = props.dashLength ?? 6;
    const dotsDashGap = props.dotsDashGap ?? 3;

    // Calculate dot size based on the size prop to ensure proportional scaling
    const dotSize = size * 2;
    const dotSpacing = dotSize + dotsDashGap;

    return (
        <div
            className={props.className}
            css={{
                flexShrink: 0,
                width: fillParentWidth ? "100%" : undefined,
                padding: paddingToCssValue(props.padding),
                // Use different styling approach based on line style
                ...(lineStyle === "solid"
                    ? {
                          height: size,
                          backgroundColor: color,
                      }
                    : lineStyle === "dashed"
                      ? {
                            height: size,
                            backgroundColor: "transparent",
                            backgroundImage: `repeating-linear-gradient(to right, ${color} 0, ${color} ${dashLength}px, transparent ${dashLength}px, transparent ${dashLength + dotsDashGap}px)`,
                            backgroundSize: undefined,
                            backgroundPosition: "center",
                            backgroundRepeat: "repeat-x",
                        }
                      : {
                            // Dotted style
                            height: dotSize,
                            backgroundColor: "transparent",
                            backgroundImage: `radial-gradient(circle, ${color} ${size}px, transparent ${size}px)`,
                            backgroundSize: `${dotSpacing}px ${dotSize}px`,
                            backgroundPosition: "0 center",
                            backgroundRepeat: "repeat-x",
                        }),
            }}
        />
    );
};

export { HorizontalLine };
export type { LineStyle };
